import { t } from '@lingui/core/macro';
import { BookingCarForCustomer, BookingRecordDetailsCustomer } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BrandType } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { NoddiDropdown, NoddiDropdownOption, NoddiSkeleton } from 'noddi-ui-web';
import { getCarDisplayName } from 'noddi-util';

import { useState } from 'react';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';
import { CarInspectionAndMeasurement } from './CarInspectionAndMeasurement';

export const BookingInspection = ({ booking }: { booking: BookingRecordDetailsCustomer }) => {
  const [selectedBookingCar, setSelectedBookingCar] = useState<BookingCarForCustomer | undefined>(
    booking.bookingItemsCar[0] ?? undefined
  );

  // By slug, not booking item: customers open this page from an SMS link without logging in.
  const {
    data: bookingInspections,
    isLoading: isBookingInspectionsLoading,
    error: bookingInspectionsError,
    refetch: refetchBookingInspections,
    isRefetching: isRefetchingBookingInspections
  } = noddiAsync.useGet({
    type: URLKeys.getBookingInspectionSessionsFromSlug,
    input: { slug: booking.slug },
    queryConfig: {
      enabled: !!booking.completedAt
    }
  });

  if (isBookingInspectionsLoading) {
    return (
      <div className='flex flex-col gap-4'>
        <NoddiSkeleton className='h-24 w-full' />
        <NoddiSkeleton className='h-24 w-full' />
      </div>
    );
  }

  if (bookingInspectionsError) {
    return (
      <ApiErrorMessageWithTrans
        error={bookingInspectionsError}
        onRetry={() => void refetchBookingInspections()}
        isRetrying={isRefetchingBookingInspections}
      />
    );
  }

  const hasMultipleCars = booking.bookingItemsCar.length > 1;

  const dropdownOptions: NoddiDropdownOption[] = booking.bookingItemsCar.map((bookingCar) => ({
    value: bookingCar.id.toString(),
    label: getCarDisplayName({ car: bookingCar })
  }));

  const selectedOption = selectedBookingCar
    ? {
        value: selectedBookingCar.id.toString(),
        label: getCarDisplayName({ car: selectedBookingCar })
      }
    : undefined;

  const handleDropdownSelect = (option: NoddiDropdownOption) => {
    setSelectedBookingCar(booking.bookingItemsCar.find((bookingCar) => bookingCar.id.toString() === option.value));
  };

  return (
    <div className='flex flex-col gap-5'>
      {hasMultipleCars && (
        <NoddiDropdown
          label={t`Select car to view`}
          options={dropdownOptions}
          placeholder={t`Select a car`}
          selectedOption={selectedOption}
          onSelect={handleDropdownSelect}
        />
      )}

      {selectedBookingCar && (
        <div className={cn('flex flex-col gap-5')}>
          <CarInspectionAndMeasurement
            bookingCar={selectedBookingCar}
            sessions={(bookingInspections?.results ?? []).filter(
              (session) => session.bookingItemId === selectedBookingCar.bookingItemId
            )}
            brandDomain={booking.serviceDepartmentBrand.domain as BrandType}
          />
        </div>
      )}
    </div>
  );
};
